/**
 * Maps landing UTM values onto CMS-configured Salesforce API names for Dynamic Form submit.
 * Isolated from Contact Us / RFQ (no shared BE mapper).
 */

import { DynamicFormUtmValues } from "@iewcasia/Utilities/dynamicFormUtmStorage";

const salesforceApiNamePattern = /^[A-Za-z][A-Za-z0-9_]*$/;

export interface DynamicFormUtmDestinations {
    sourceMediumField: string;
    campaignField: string;
    channelGroupField: string;
}

/** Combines utm_source and utm_medium into Salesforce Session_Source_Medium format. */
function buildSourceMedium(source: string, medium: string): string {
    if (source && medium) {
        return `${source} / ${medium}`;
    }
    return source || medium || "";
}

/** Same channel rules as Contact Us, implemented here so Dynamic Form stays isolated. */
export function deriveDynamicFormChannelGroup(medium: string): string {
    if (!medium || medium === "(none)" || medium === "(not set)") {
        return "Direct";
    }

    const mediumLower = medium.toLowerCase();

    if (mediumLower.includes("email")) {
        return "Email";
    }
    if (mediumLower.includes("cpc") || mediumLower.includes("ppc") || mediumLower.includes("paidsearch")) {
        return "Paid Search";
    }
    if (mediumLower.includes("social") && mediumLower.includes("paid")) {
        return "Paid Social";
    }
    if (mediumLower.includes("social")) {
        return "Organic Social";
    }
    if (mediumLower.includes("organic")) {
        return "Organic Search";
    }
    if (mediumLower.includes("referral")) {
        return "Referral";
    }

    return "Direct";
}

/** Writes a value onto `fields` when the Salesforce API name is valid and the value is non-empty. */
function setIfValid(
    fields: Record<string, string | boolean>,
    apiName: string,
    value: string,
) {
    const key = (apiName || "").trim();
    if (!key || !value || !salesforceApiNamePattern.test(key)) {
        return;
    }
    fields[key] = value;
}

/**
 * Writes transformed UTM values into the submit `fields` dictionary using CMS destination API names.
 * Empty values are omitted. Overwrites the same keys if already present.
 */
export function applyDynamicFormUtmToFields(
    fields: Record<string, string | boolean>,
    utm: DynamicFormUtmValues | null | undefined,
    destinations: DynamicFormUtmDestinations,
) {
    if (!utm) {
        return;
    }

    const source = (utm.utmSource || "").trim();
    const medium = (utm.utmMedium || "").trim();
    const campaign = (utm.utmCampaign || "").trim();

    const sourceMedium = buildSourceMedium(source, medium);
    setIfValid(fields, destinations.sourceMediumField, sourceMedium);
    setIfValid(fields, destinations.campaignField, campaign);

    if (source || medium || campaign) {
        setIfValid(fields, destinations.channelGroupField, deriveDynamicFormChannelGroup(medium));
    }
}
